import isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';
import {
  sanitizeArea,
  sanitizeLocation,
} from '../../../routes/searchResult/refinements/services/sanitize';
import {
  convertBoundingBoxToString as convertBoundingBoxToQueryString,
  convertCurrentLocationToQueryObj,
  convertSortToString,
  convertKeywordsToQueryString,
  convertPropertyTypeUrlsToQueryString,
  validatedMapView,
  validatedPage,
  convertLocationsToString as convertLocationsToQueryString,
} from '../../../routes/searchResult/refinements/services/queryGenerator';
import type { ExtendedRefinementsType, LocationDisplayType } from '../constants/filterTypes';
import { stripUndefinedProperties } from '../../stripUndefinedProperties';

export const locationUrlGenerator = (locations: string[] = []) => {
  const validLocations = locations.map(sanitizeLocation).filter(Boolean);
  const singleValidLocation = validLocations.length === 1;

  return singleValidLocation ? validLocations[0] : '';
};

const addCommas = (s: string) => s.replace(/(%2c)+/gi, ',');

export function searchPathGenerator(
  channel?: string,
  locations?: string,
  propertyTypes?: string[],
) {
  const singularPropertyType = propertyTypes && propertyTypes.length === 1 && propertyTypes[0];

  if (isEmpty(propertyTypes) && isEmpty(locations)) {
    return `/${channel}/`;
  }

  const path = [channel, locations, singularPropertyType]
    .filter(Boolean)
    .join('/')
    .replace(/\*/g, '');

  return `/${path}/`;
}

const getLocationDisplayValues = (locations?: LocationDisplayType[]) =>
  locations?.map((location) => location.displayValue);

export type SearchURLPartsType = { pathname: string; search: string };

export const generateSearchUrl = ({
  channelUrl,
  propertyTypes,
  locations,
  minPrice,
  maxPrice,
  tenureTypeUrl,
  minFloorArea,
  maxFloorArea,
  minSiteArea,
  maxSiteArea,
  numParkingSpaces,
  searchWithin,
  sortObject,
  keywords,
  currentLocation,
  energyEfficiency,
  mapView,
  includePropertiesWithin,
  page,
  boundingBox,
}: ExtendedRefinementsType): SearchURLPartsType => {
  const locationDisplayValues = getLocationDisplayValues(locations);
  const locationInPath = boundingBox ? undefined : locationUrlGenerator(locationDisplayValues);
  const locationsInQuery = boundingBox
    ? undefined
    : convertLocationsToQueryString(locationDisplayValues);
  const propertyTypeUrls = propertyTypes ? propertyTypes.map((p) => p.url) : undefined;
  const propertyTypesQueryStr = convertPropertyTypeUrlsToQueryString(propertyTypeUrls);

  const queryObj = {
    locations: locationsInQuery,
    boundingBox: convertBoundingBoxToQueryString(boundingBox),
    minFloorArea: sanitizeArea(minFloorArea) || undefined,
    maxFloorArea: sanitizeArea(maxFloorArea) || undefined,
    minSiteArea: sanitizeArea(minSiteArea) || undefined,
    maxSiteArea: sanitizeArea(maxSiteArea) || undefined,
    ...convertCurrentLocationToQueryObj(currentLocation),
    numParkingSpaces: numParkingSpaces || undefined,
    includePropertiesWithin: includePropertiesWithin || searchWithin,
    activeSort: convertSortToString(sortObject),
    keywords: convertKeywordsToQueryString(keywords),
    energyEfficiency: energyEfficiency || undefined,
    tenure: tenureTypeUrl,
    minPrice: minPrice || undefined,
    maxPrice: maxPrice || undefined,
    propertyTypes: propertyTypesQueryStr,
    mapView: validatedMapView(mapView),
    page: validatedPage(page),
  };

  const search = queryString.stringify(stripUndefinedProperties(queryObj));
  const searchWithLocationCommas = addCommas(search);

  return {
    pathname: searchPathGenerator(channelUrl, locationInPath, propertyTypeUrls),
    search: searchWithLocationCommas,
  };
};
